import React, { useEffect, useState } from 'react';
import { Filter, Download, Inbox, Loader2, RefreshCw, Search, X, ChevronDown, Eye, CheckCircle2, Clock, FileText, Trash2 } from 'lucide-react';
import { submissionsApi, SubmissionRecord, newslettersApi, NewsletterRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
type StatusFilter = 'all' | 'Not Subscribed' | 'Confirmed';
/** Parse Form_Payload JSON safely */
function parsePayload(raw: string): Record<string, any> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
/** Extract email from payload (tries common field names) */
function extractEmail(payload: Record<string, any> | null): string {
  if (!payload) return '-';
  return payload.email || payload.Email || payload.EMAIL || payload.e_mail || '-';
}
/** Extract a display name from payload */
function extractName(payload: Record<string, any> | null): string {
  if (!payload) return '';
  const first = payload.firstName || payload.first_name || payload.FirstName || payload.name || '';
  const last = payload.lastName || payload.last_name || payload.LastName || '';
  return [first, last].filter(Boolean).join(' ');
}
export function Submissions() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsData, nlData] = await Promise.all([submissionsApi.list(), newslettersApi.list()]);
      setSubmissions(subsData);
      setNewsletters(nlData);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  // Resolve newsletter name from Newsletter_ID
  const getNewsletterName = (newsletterId: string): string => {
    const nl = newsletters.find(n => n.Newsletter_ID === newsletterId);
    return nl?.Newsletter || newsletterId || '-';
  };
  // Filter by workspace scope
  const scopedSubmissions = submissions.filter(s => {
    if (s.Team !== currentWorkspace.name) return false;
    if (currentSubRegion) {
      return !s.Region || s.Region === currentSubRegion;
    }
    return true;
  });
  // Apply status filter
  const statusFiltered = scopedSubmissions.filter(s => {
    if (statusFilter === 'all') return true;
    return s.Status === statusFilter;
  });
  // Apply search
  const filteredSubmissions = statusFiltered.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const payload = parsePayload(s.Form_Payload);
    const email = extractEmail(payload).toLowerCase();
    const name = extractName(payload).toLowerCase();
    const nlName = getNewsletterName(s.Newsletter_ID).toLowerCase();
    return email.includes(q) || name.includes(q) || nlName.includes(q) || s.ID.toLowerCase().includes(q);
  });
  // Sort by Added_Time descending (latest first)
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    const dateA = a.Added_Time ? new Date(a.Added_Time).getTime() : 0;
    const dateB = b.Added_Time ? new Date(b.Added_Time).getTime() : 0;
    return dateB - dateA;
  });
  const showConfirmedAt = statusFilter === 'Confirmed';
  // Counts for filter tabs
  const allCount = scopedSubmissions.length;
  const pendingCount = scopedSubmissions.filter(s => s.Status === 'Not Subscribed').length;
  const confirmedCount = scopedSubmissions.filter(s => s.Status === 'Confirmed').length;
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await submissionsApi.delete(id);
      setSubmissions(prev => prev.filter(s => s.ID !== id));
      setDeleteConfirm(null);
      toast.success('Submission deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete submission', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-4479">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4480">
        <div className="min-w-0" data-id="element-4481">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4482">
            Form Submissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4483">
            All form submissions from your signup forms, pulled from Zoho
            Creator.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0" data-id="element-4484">
          <button onClick={fetchData} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4485">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4486" />
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3" data-id="element-4487">
        {/* Status filter tabs */}
        <div className="flex bg-gray-100 rounded-lg p-0.5" data-id="element-4488">
          <button onClick={() => setStatusFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4489">
            All ({allCount})
          </button>
          <button onClick={() => setStatusFilter('Not Subscribed')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'Not Subscribed' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4490">
            <span className="flex items-center gap-1.5" data-id="element-4491">
              <Clock className="h-3.5 w-3.5" data-id="element-4492" />
              Pending ({pendingCount})
            </span>
          </button>
          <button onClick={() => setStatusFilter('Confirmed')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'Confirmed' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4493">
            <span className="flex items-center gap-1.5" data-id="element-4494">
              <CheckCircle2 className="h-3.5 w-3.5" data-id="element-4495" />
              Confirmed ({confirmedCount})
            </span>
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm" data-id="element-4496">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4497" />
          <input type="text" placeholder="Search by email, name, or newsletter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4498" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4499">
              <X className="h-3.5 w-3.5" data-id="element-4500" />
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4501">
        {loading && submissions.length === 0 ? <div className="p-12 text-center" data-id="element-4502">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4503" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-4504">Loading submissions...</p>
          </div> : filteredSubmissions.length === 0 ? <div className="p-12 text-center" data-id="element-4505">
            <Inbox className="mx-auto h-12 w-12 text-gray-400" data-id="element-4506" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4507">
              {searchQuery || statusFilter !== 'all' ? 'No matching submissions' : 'No submissions yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-4508">
              {searchQuery || statusFilter !== 'all' ? 'Try adjusting your search or filter.' : 'Submissions will appear here when customers fill out your signup forms.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-4509">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-4510">
              <thead className="bg-gray-50" data-id="element-4511">
                <tr data-id="element-4512">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4513">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4514">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4515">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4516">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4517">
                    Added Time
                  </th>
                  {showConfirmedAt && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4518">
                      Confirmed At
                    </th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4519">
                    Region
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4520">
                    Payload
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-4521">
                {sortedSubmissions.map(sub => {
              const payload = parsePayload(sub.Form_Payload);
              const email = extractEmail(payload);
              const name = extractName(payload);
              return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-4522">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4523">
                        <span className="text-sm font-medium text-gray-900 truncate block max-w-[220px]" data-id="element-4524">
                          {email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4525">
                        <span className="text-sm text-gray-600 truncate block max-w-[150px]" data-id="element-4526">
                          {name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4527">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" data-id="element-4528">
                          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4529" />
                          {getNewsletterName(sub.Newsletter_ID)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4530">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${sub.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-4531">
                          {sub.Status === 'Confirmed' ? <CheckCircle2 className="h-3 w-3" data-id="element-4532" /> : <Clock className="h-3 w-3" data-id="element-4533" />}
                          {sub.Status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4534">
                        {sub.Added_Time ? new Date(sub.Added_Time).toLocaleString() : '-'}
                      </td>
                      {showConfirmedAt && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4535">
                          {sub.Confirmed_At1 ? new Date(sub.Confirmed_At1).toLocaleString() : '-'}
                        </td>}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4536">
                        {sub.Region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-4537">
                        <div className="flex items-center justify-end gap-1" data-id="element-4538">
                          <button onClick={() => setSelectedSubmission(sub)} className="text-blue-600 hover:text-blue-900 text-sm inline-flex items-center gap-1 p-1.5 hover:bg-blue-50 rounded" data-id="element-4539">
                            <Eye className="h-3.5 w-3.5" data-id="element-4540" />
                            View
                          </button>
                          <button onClick={() => setDeleteConfirm(sub.ID)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4541">
                            <Trash2 className="h-3.5 w-3.5" data-id="element-4542" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Payload Modal */}
      {selectedSubmission && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4543">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4544">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSubmission(null)} data-id="element-4545" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-4546">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-4547">
                <div data-id="element-4548">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4549">
                    Submission Payload
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-4550">
                    ID: {selectedSubmission.ID}
                  </p>
                </div>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-md" data-id="element-4551">
                  <X className="h-5 w-5" data-id="element-4552" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-4553">
                {/* Status */}
                <div className="flex items-center justify-between" data-id="element-4554">
                  <span className="text-sm font-medium text-gray-700" data-id="element-4555">
                    Status
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${selectedSubmission.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-4556">
                    {selectedSubmission.Status === 'Confirmed' ? <CheckCircle2 className="h-3 w-3" data-id="element-4557" /> : <Clock className="h-3 w-3" data-id="element-4558" />}
                    {selectedSubmission.Status}
                  </span>
                </div>

                {/* Newsletter */}
                <div className="flex items-center justify-between" data-id="element-4559">
                  <span className="text-sm font-medium text-gray-700" data-id="element-4560">
                    Newsletter
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-4561">
                    {getNewsletterName(selectedSubmission.Newsletter_ID)}
                  </span>
                </div>

                {/* Parsed fields */}
                {(() => {
              const payload = parsePayload(selectedSubmission.Form_Payload);
              if (!payload) return <div className="text-sm text-red-500" data-id="element-4562">
                        Could not parse payload
                      </div>;
              return <div className="space-y-2" data-id="element-4563">
                      <h4 className="text-sm font-medium text-gray-700" data-id="element-4564">
                        Form Fields
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-4565">
                        {Object.entries(payload).filter(([key]) => key !== 'newsletter_id' && key !== 'submitted_at').map(([key, value]) => <div key={key} className="flex items-center justify-between px-3 py-2" data-id="element-4566">
                              <span className="text-xs font-medium text-gray-500" data-id="element-4567">
                                {key}
                              </span>
                              <span className="text-sm text-gray-900 text-right max-w-[250px] truncate" data-id="element-4568">
                                {String(value)}
                              </span>
                            </div>)}
                      </div>

                      {payload.submitted_at && <div className="flex items-center justify-between pt-2" data-id="element-4569">
                          <span className="text-xs text-gray-400" data-id="element-4570">
                            Submitted at
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-4571">
                            {new Date(payload.submitted_at).toLocaleString()}
                          </span>
                        </div>}
                    </div>;
            })()}

                {/* Raw JSON */}
                <details className="group" data-id="element-4572">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600" data-id="element-4573">
                    View raw JSON
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48" data-id="element-4574">
                    {JSON.stringify(parsePayload(selectedSubmission.Form_Payload), null, 2) || selectedSubmission.Form_Payload}
                  </pre>
                </details>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-4575">
                <button onClick={() => setSelectedSubmission(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4576">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4577">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4578">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4579" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-4580">
              <div className="flex items-center gap-4 mb-4" data-id="element-4581">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4582">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-4583" />
                </div>
                <div data-id="element-4584">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4585">
                    Delete Submission
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-4586">
                    Are you sure? This will permanently remove this submission
                    record from Zoho Creator.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-4587">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4588">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4589">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4590" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}